import { CircleCheck, Eye } from 'lucide-react';
import type { FirewallRulesetVersionSummary } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { formatLocalTimestamp } from './formatters.ts';
import {
  Button,
  Chip,
  IconButton,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Version history for one ruleset (#1760).
 *
 * Every row is immutable and inert except the one `active_version` points at.
 * "Roll back" is therefore not a distinct operation with its own semantics — it
 * is activating an older row, and it is spelled the same way here so nobody has
 * to learn a second mental model under incident pressure.
 *
 * `resolved_hash` is shown truncated but in full on hover: it is the identity
 * the runtime and the replay record agree on, and it is what an operator quotes
 * when asking whether the thing running is the thing they think is running.
 *
 * The armed row's button stays live and reads "re-arm". Re-activating the
 * version already armed is one of the two paths the replay gate deliberately
 * leaves open (#1759), because it is what an operator reaches for when they
 * need to be certain of what is running; a disabled button there would take an
 * ungated recovery path away.
 */

const HASH_PREVIEW_LENGTH = 12;

export function FirewallVersionHistory({
  versions,
  activeVersion,
  selectedVersion,
  activating,
  onSelect,
  onActivate,
}: {
  readonly versions: readonly FirewallRulesetVersionSummary[];
  readonly activeVersion: number | null;
  readonly selectedVersion: number | null;
  readonly activating: boolean;
  readonly onSelect: (version: number) => void;
  readonly onActivate: (version: number) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Table className="min-w-0" testId="firewall-version-history">
      <THead>
        <Tr interactive={false}>
          <Th>{t('firewall_rulesets.version.col.version')}</Th>
          <Th>{t('firewall_rulesets.version.col.state')}</Th>
          <Th>{t('firewall_rulesets.version.col.note')}</Th>
          <Th>{t('firewall_rulesets.version.col.hash')}</Th>
          <Th>{t('firewall_rulesets.version.col.author')}</Th>
          <Th className="text-right">{t('firewall_rulesets.version.col.actions')}</Th>
        </Tr>
      </THead>
      <TBody>
        {versions.length === 0 ? (
          <TableEmpty colSpan={6}>{t('firewall_rulesets.version.empty')}</TableEmpty>
        ) : versions.map((version) => {
          const isActive = version.version === activeVersion;
          const isSelected = version.version === selectedVersion;
          return (
            <Tr key={version.version} className={isSelected ? 'bg-raised' : undefined}>
              <Td className="whitespace-nowrap font-mono text-label text-paper">
                v{version.version}
              </Td>
              <Td>
                {isActive ? (
                  <Chip tone="ok">{t('firewall_rulesets.version.state.active')}</Chip>
                ) : (
                  <Chip tone="neutral" lamp={false}>
                    {t('firewall_rulesets.version.state.draft')}
                  </Chip>
                )}
              </Td>
              <Td className="max-w-[280px]">
                <span className="text-label text-paper-mid">{version.note ?? '—'}</span>
              </Td>
              <Td>
                <span
                  className="font-mono text-micro text-paper-low"
                  title={version.resolved_hash}
                >
                  {version.resolved_hash.slice(0, HASH_PREVIEW_LENGTH)}
                </span>
              </Td>
              <Td className="max-w-[220px]">
                <div className="flex flex-col gap-0.5">
                  <span className="truncate text-label text-paper-mid">
                    {version.created_by ?? t('firewall_rulesets.version.author_unknown')}
                  </span>
                  <span className="whitespace-nowrap font-mono text-micro text-paper-low">
                    {formatLocalTimestamp(version.created_at, { seconds: true })}
                  </span>
                </div>
              </Td>
              <Td className="whitespace-nowrap text-right">
                <span className="inline-flex items-center justify-end gap-1">
                  <IconButton
                    size="sm"
                    label={t('firewall_rulesets.version.action.inspect')}
                    onClick={() => onSelect(version.version)}
                    data-testid={`firewall-version-inspect-${String(version.version)}`}
                  >
                    <Eye aria-hidden="true" className="h-4 w-4" />
                  </IconButton>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onActivate(version.version)}
                    disabled={activating}
                    data-testid={`firewall-version-activate-${String(version.version)}`}
                  >
                    <CircleCheck aria-hidden="true" className="h-4 w-4" />
                    <span>
                      {isActive
                        ? t('firewall_rulesets.version.action.rearm')
                        : t('firewall_rulesets.version.action.activate')}
                    </span>
                  </Button>
                </span>
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
}
